import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';
import { Member } from '../../models/member.model';
import * as memberActions from '../actions/member.actions';

export interface MemberState extends EntityState<Member> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  memberStatsLoading: boolean;
  error: AppExceptionModel | undefined;
}

export const memberAdapter: EntityAdapter<Member> =
  createEntityAdapter<Member>();

export const initialState: MemberState = memberAdapter.getInitialState({
  selectedMemberId: undefined,
  lastSearchKey: undefined,
  loading: false,
  loaded: false,
  inProgress: false,
  memberStatsLoading: false,
  error: undefined,
});

export const memberReducer = createReducer(
  initialState,
  on(memberActions.getMembers, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(memberActions.getMember, (state, {}) => ({
    ...state,
    error: undefined,
  })),
  on(memberActions.getMembersDone, (state, {}) => ({
    ...state,
    loading: false,
    error: undefined,
  })),
  on(memberActions.addMemberToStore, (state, { member }) =>
    memberAdapter.upsertOne(member, {
      ...state,
      error: undefined,
      loading: false,
      loaded: true,
    }),
  ),
  on(memberActions.addMembersToStore, (state, { members }) =>
    memberAdapter.upsertMany(members, {
      ...state,
      loading: false,
      loaded: true,
      inProgress: false,
      error: undefined,
    }),
  ),
  on(memberActions.loadMember, (state, { members }) => {
    if (members?.length > 0) {
      return memberAdapter.upsertMany(members, {
        ...state,
        loading: false,
        loaded: true,
        inProgress: false,
        error: undefined,
      });
    } else {
      return {
        ...state,
        loading: false,
        loaded: true,
        inProgress: false,
        error: undefined,
      };
    }
  }),
  on(memberActions.getMembersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(memberActions.create, (state, { member }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(memberActions.createSuccess, (state, { member }) =>
    memberAdapter.addOne(member, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(memberActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(memberActions.update, (state, { member }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(memberActions.updateSuccess, (state, { member }) =>
    memberAdapter.upsertOne(member, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(memberActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(memberActions.updateStatus, (state, { member }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(memberActions.updateStatusSuccess, (state, { member }) =>
    memberAdapter.upsertOne(member, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(memberActions.updateStatusFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(memberActions.deleteAction, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(memberActions.deleteSuccess, (state, { id }) =>
    memberAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(memberActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(memberActions.getMemberStats, (state) => ({
    ...state,
    memberStatsLoading: true,
  })),
  on(memberActions.getMemberStatsDone, (state) => ({
    ...state,
    memberStatsLoading: false,
  })),
  on(memberActions.getMemberStatsFailure, (state) => ({
    ...state,
    memberStatsLoading: false,
  })),
  on(memberActions.requestMembership, (state) => ({
    ...state,
    inProgress: true,
  })),
  on(memberActions.requestMembershipSuccess, (state) => ({
    ...state,
    inProgress: false,
  })),
  on(memberActions.requestMembershipFailure, (state) => ({
    ...state,
    inProgress: false,
  })),
  on(memberActions.clear, (state) => initialState),
);
